import React, { useState, useRef, useEffect } from 'react'
import { ScreenService } from '@/utils/services/ScreenService.js'
import screenService from '@/utils/services/ScreenService.js'
import { useAuth } from '@/contexts/AuthContext'

function EditImageModal({ screen, onClose, onEdit, allScreens, screenVersion }) {
  const [name, setName] = useState(screen.name)
  const [imageUrl, setImageUrl] = useState(screen.imageUniversalUrl || screen.imageUrl || '')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const fileInputRef = useRef()
  const [newFile, setNewFile] = useState(null)
  const [angle, setAngle] = useState(screen.angle || 0)
  const { currentUser } = useAuth()

  // Prevent background scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  // Convert file to base64 data URL
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Get aspect ratio based on angle
  const getImageAspectRatio = (angle) => {
    switch(angle) {
      case 0:
      case 180:
        return "16/9";
      case 90:
      case 270:
        return "9/16";
      default:
        return "16/9";
    }
  };

  // Rotate left (counter-clockwise)
  const rotateLeft = () => {
    setAngle(prevAngle => {
      const newAngle = (prevAngle + 90) % 360;
      return newAngle;
    });
  };

  // Rotate right (clockwise)
  const rotateRight = () => {
    setAngle(prevAngle => {
      const newAngle = (prevAngle - 90 + 360) % 360;
      return newAngle;
    });
  };

  const handleSubmit = async () => {
    setError('')
    if (!name.trim()) {
      setError('Please provide the screen name.')
      return
    }
    if (allScreens.some(s => s.name === name.trim() && s.key !== screen.key)) {
      setError(`A screen with the name '${name.trim()}' already exists.`)
      return
    }
    setUploading(true)
    try {
      let imageDataUrl = null
      if (newFile) {
        imageDataUrl = await fileToBase64(newFile)
      }
      // Prepare screenData
      const screenData = {
        ...screen,
        name: name.trim(),
        angle: angle
      }
      // Remove key from screenData if present (since it's passed separately)
      delete screenData.key
      await screenService.modifyScreen(
        screen.key,
        screenData,
        imageDataUrl || imageUrl || '',
        currentUser?.uid
      )
      // Fetch the updated screen and update parent
      const updatedScreen = await screenService.getScreen(screen.key)
      onEdit(updatedScreen)
    } catch (err) {
      setError('Failed to update screen: ' + (err.message || err))
    } finally {
      setUploading(false)
    }
  }

  const handleSelectImage = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setNewFile(file)
    setImageUrl(URL.createObjectURL(file))
  }

  const handleDownload = () => {
    if (!imageUrl) return;
    window.open(imageUrl, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999]" style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', margin: 0, padding: 0 }}>
      <div className="bg-white rounded-xl border border-gray-200 shadow-2xl w-[1350px] h-auto max-w-7xl flex flex-col overflow-visible">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100 flex-shrink-0">
          <span className="font-bold text-2xl text-gray-900">{name}</span>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors" aria-label="Close">
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Content: Two column layout */}
        <div className="flex flex-row px-0 py-0 bg-gray-50 flex-1 overflow-hidden min-h-0 min-w-0 h-full">
          {/* Left: Screen preview */}
          <div className="flex-1 min-w-0 px-8 py-8 bg-white flex items-center justify-center">
            {imageUrl ? (
              <div className="relative">
                {(angle === 0 || angle === 180 || !angle) ? (
                  <>
                    {/* Landscape TV Frame (0°, 180°, or no angle) */}
                    <div 
                      className="relative border-2 border-black inline-block p-1 m-1 overflow-hidden transition-transform duration-300 bg-gradient-to-br from-yellow-50 to-yellow-100 shadow-2xl rounded-lg"
                      style={{
                        borderBottomWidth: (angle === 0 || !angle) ? '20px' : '2px',
                        borderRightWidth: '2px',
                        borderTopWidth: angle === 180 ? '20px' : '2px',
                        borderLeftWidth: '2px',
                        width: '600px',
                        height: '400px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1), 0 0 20px rgba(255, 193, 7, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                        background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 50%, #fde68a 100%)',
                        borderColor: '#1f2937',
                        borderStyle: 'solid'
                      }}
                    >
                      <div className="overflow-hidden flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-gray-900 to-gray-800 p-1 relative">
                        <img 
                          src={ScreenService.getThumbnail(imageUrl)} 
                          alt="Screen preview" 
                          className="w-full h-full object-contain rounded-lg"
                          style={{
                            transition: 'transform 0.3s ease',
                            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                          }}
                        />
                      </div>
                    </div>
                    {/* TV Power Button - Separate Element */}
                    {(angle === 0 || angle === 180 || !angle) && (
                      <div 
                        className="absolute z-30"
                        style={{
                          bottom: (angle === 0 || !angle) ? '4px' : 'auto',
                          top: angle === 180 ? '4px' : 'auto',
                          left: '50%',
                          transform: 'translateX(-50%)'
                        }}
                      >
                        <div className="w-4 h-4 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full border border-gray-400 shadow-md flex items-center justify-center">
                          <div className="w-1 h-1 bg-red-400 rounded-full shadow-inner"></div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Portrait TV Frame (90° and 270°) */}
                    <div 
                      className="relative border-2 border-black inline-block p-1 m-1 overflow-hidden transition-transform duration-300 bg-gradient-to-br from-yellow-50 to-yellow-100 shadow-2xl rounded-lg"
                      style={{
                        borderBottomWidth: '2px',
                        borderRightWidth: angle === 270 ? '20px' : '2px',
                        borderTopWidth: '2px',
                        borderLeftWidth: angle === 90 ? '20px' : '2px',
                        width: '400px',
                        height: '600px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1), 0 0 20px rgba(255, 193, 7, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                        background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 50%, #fde68a 100%)',
                        borderColor: '#1f2937',
                        borderStyle: 'solid'
                      }}
                    >
                      <div className="overflow-hidden flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-gray-900 to-gray-800 p-1 relative">
                        <img 
                          src={ScreenService.getThumbnail(imageUrl)} 
                          alt="Screen preview" 
                          className="w-full h-full object-contain rounded-lg"
                          style={{
                            transition: 'transform 0.3s ease',
                            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400 text-lg">
                No image selected
              </div>
            )}
          </div>
          {/* Right: Controls panel */}
          <div className="w-[330px] flex flex-col items-start gap-4 border-l border-gray-200 pl-8 pr-4 py-8 h-full bg-gray-50 flex-shrink-0">
            <div className="font-semibold text-lg text-gray-800 mb-2">Screen Settings</div>
            <div className="w-full">
              <label className="block mb-3 font-medium text-gray-700 text-base">Screen Name</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-4 py-3 mb-4 text-base focus:outline-none focus:ring-2 focus:ring-yellow-400"
                placeholder="Enter screen name"
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={uploading}
                maxLength={64}
              />
            </div>

            <div className="w-full">
              <label className="block mb-3 font-medium text-gray-700 text-base">Image Controls</label>
              <div className="space-y-3">
                <button
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-base font-medium"
                  onClick={handleSelectImage}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Change Image'}
                </button>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
                <button
                  className="w-full px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-base font-medium"
                  onClick={handleDownload}
                  disabled={!imageUrl}
                >
                  Open in New Tab
                </button>
              </div>
            </div>
            {/* Rotation Controls */}
            <div className="w-full">
              <div className="flex items-center justify-between mb-3">
                <label className="font-medium text-gray-700 text-base">TV Orientation</label>
                <div className="btn-group">
                  <button
                    type="button"
                    className="px-4 py-2 bg-blue-600 text-white rounded-l-lg hover:bg-blue-700 transition-colors text-base font-medium"
                    onClick={rotateLeft}
                    disabled={uploading}
                    title="Rotate Left"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-colors text-base font-medium"
                    onClick={rotateRight}
                    disabled={uploading}
                    title="Rotate Right"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            {error && <div className="text-red-500 text-sm">{error}</div>}
            <div className="w-full mt-auto pt-6 border-t border-gray-200">
              <div className="flex gap-3">
                <button
                  className="flex-1 px-4 py-3 rounded-lg text-base text-gray-700 hover:bg-gray-100 transition-colors"
                  onClick={onClose}
                  disabled={uploading}
                >
                  Close
                </button>
                <button
                  className="flex-1 px-4 py-3 rounded-lg bg-yellow-500 text-white text-base font-semibold hover:bg-yellow-600 transition-colors disabled:opacity-60"
                  onClick={handleSubmit}
                  disabled={!name.trim() || uploading}
                >
                  {uploading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditImageModal 